import { normalizeDob, normalizeText as normalize } from './claim-editor-linked-ids';

/**
 * Whether a claim's `subscriberId` still describes the person currently typed into
 * the subscriber form.
 *
 * `CreateClaimSubscriber.subscriberId` is a REFERENCE, not a hint: the API contract
 * says "if included, it will lookup the subscriber by id and ignore any other
 * subscriber fields". So a payload carrying a stale id plus an edited name transmits
 * the OLD subscriber while every UI surface — the form, the payload diff — shows the
 * new one. That is how a claim went out under DALILA RIVERA after the subscriber had
 * been retyped as JUAN SANTANA.
 *
 * The id is therefore only safe to send while the identity-bearing fields still match
 * the record it points at. When they diverge, drop it and let the backend take its
 * "create or lookup by fields" path.
 *
 * `payerId`, `memberId` and `relationshipToPatient` are deliberately NOT part of the
 * identity: they describe the POLICY, not the person, so changing which payer a
 * subscriber is billed under must not unlink the subscriber record.
 */
export interface SubscriberIdentity {
    first?: string | null;
    middle?: string | null;
    last?: string | null;
    suffix?: string | null;
    dob?: unknown;
    sex?: string | null;
    ssn?: string | null;
    phone?: string | null;
    /**
     * Compared as a compacted list, because that is how the payload stores it: the form's
     * two address inputs become `[address1, address2].filter(Boolean)`, so a blank first
     * line is not a different address from the same street on the second line.
     */
    lines?: Array<string | null | undefined>;
    city?: string | null;
    state?: string | null;
    postal?: string | null;
    country?: string | null;
}

/** A stable string for two identities to be compared by value. */
export function subscriberIdentityKey(identity: SubscriberIdentity): string {
    return [
        normalize(identity.first),
        normalize(identity.middle),
        normalize(identity.last),
        normalize(identity.suffix),
        normalizeDob(identity.dob),
        normalize(identity.sex),
        normalize(identity.ssn),
        normalize(identity.phone),
        (identity.lines ?? []).map(normalize).filter(Boolean).join('\u0001'),
        normalize(identity.city),
        normalize(identity.state),
        normalize(identity.postal),
        // The payload defaults a blank country to US, so the form's "" and a stored "US"
        // are the same address. Without this the id unlinked itself one save later.
        normalize(identity.country) || 'US',
    ].join('\u0000');
}

/** Reads an identity out of anything shaped like a subscriber record: `addresses[0]`. */
export function subscriberIdentityFromRecord(record: any): SubscriberIdentity {
    const address = record?.addresses?.[0];
    return {
        first: record?.first,
        middle: record?.middle,
        last: record?.last,
        suffix: record?.suffix,
        dob: record?.dob,
        sex: record?.sex,
        ssn: record?.ssn,
        phone: record?.phone,
        lines: address?.lines,
        city: address?.city,
        state: address?.state,
        postal: address?.postal,
        country: address?.country,
    };
}

/** The subscriber record the form's `subscriberId` currently stands for. */
export interface LinkedSubscriber {
    id: string;
    identityKey: string;
}

/**
 * The `subscriberId` to put in the payload: the linked id while the form still
 * describes that person, and `undefined` — which `deepClean` strips from the payload
 * entirely — once it does not.
 */
export function resolveSubscriberId(
    linked: LinkedSubscriber | null | undefined,
    current: SubscriberIdentity,
): string | undefined {
    if (!linked?.id) return undefined;
    return linked.identityKey === subscriberIdentityKey(current) ? linked.id : undefined;
}
